import { Inject, Injectable, Output, EventEmitter } from '@angular/core';
import { Headers, Http } from '@angular/http';
import 'rxjs/add/operator/toPromise';
import { AdalService } from '../shared/azure-authentication/adal.service';
import { BaseEndpoint } from '../app.constants';
import { User } from './user.model';
import { Region } from './user.model';
import { LoanProgram } from '../loanprograms/loanprogram.model';
import { Audit } from '../audits/audit.model';

@Injectable()
export class UserService {
    public currentUser: User;
    public skillSet: User;

    @Output() currentUserAuthenticated: EventEmitter<User> = new EventEmitter<User>();
    @Output() userAdded: EventEmitter<User> = new EventEmitter<User>();
    @Output() userUpdated: EventEmitter<User> = new EventEmitter<User>();
    @Output() userDeleted: EventEmitter<string> = new EventEmitter<string>();

    constructor(private http: Http, @Inject(BaseEndpoint) private baseApiUrl: string, private adalService: AdalService) {
    }

    getUser(userId: string): Promise<User> {
        const url = this.baseApiUrl + 'v1/user/' + userId;
      return this.http
        .get(url, { headers: this.adalService.createServiceHeader() })
        .toPromise()
        .then(resp => {
                return resp.json().body as User;
            })
        .catch(this.handleError);
    }

    getUsersByEmail(email: string): Promise<User[]> {
        const url = this.baseApiUrl + 'v1/users' + (email ? '?email=' + email : '');
        return this.http
            .get(url, { headers: this.adalService.createServiceHeader() })
            .toPromise()
            .then(resp => {
                return resp.json().body as User[];
            })
            .catch(this.handleError);
    }

    getUsersByPersona(persona: string): Promise<User[]> {
        const url = this.baseApiUrl + 'v1/users/persona'+ (persona ? '?persona=' + persona : '');
        return this.http
            .get(url, { headers: this.adalService.createServiceHeader() })
            .toPromise()
            .then(resp => {
                return resp.json().body as User[];
            })
            .catch(this.handleError);
    }

    getUsers(): Promise<User[]> {
        const url = this.baseApiUrl + 'v1/users';
        return this.http
            .get(url, { headers: this.adalService.createServiceHeader() })
            .toPromise()
            .then(resp => {
                return resp.json().body as User[];
            })
            .catch(this.handleError);
    }

    getWhiteGloveOptions(): string[] {
        var array: string[] = ["N/A", "Transition Training LOs"];
        return array;
    }

  addUser(user: User): Promise<User> {

        const url = this.baseApiUrl + 'v1/users';
        return this.http
            .post(url, JSON.stringify(user), { headers: this.adalService.createServiceHeader() })
            .toPromise()
          .then(resp => {
              const newUser = resp.json().body as User;
                newUser.warningMessages = resp.json().messages as string[];
                this.userAdded.emit(newUser);
                return newUser;
            })
            .catch(this.handleError);
    }

    putUser(user: User, userId: string): Promise<User> {
        const url = this.baseApiUrl + 'v1/user/' + userId;
        return this.http
            .put(url, JSON.stringify(user), { headers: this.adalService.createServiceHeader() })
            .toPromise()
            .then(resp => {
                const editUser = resp.json().body as User;
                this.userUpdated.emit(editUser);
                return editUser;
            })
            .catch(resp => {
                var errors = resp.json().messages;
                if (errors)
                    return this.handleError(errors.map(({ description }) => description));
                else
                    return this.handleError(null);
            });
    }

    deleteUser(userId: string, loggedInUserEmail: string): Promise<boolean> {
        var url = this.baseApiUrl + 'v1/user/' + userId + '/' + loggedInUserEmail;
        return this.http
            .delete(url, { headers: this.adalService.createServiceHeader() })
            .toPromise()
            .then(resp => {
                var success = !resp.json().hasErrors as boolean;

                if (success) {
                    this.userDeleted.emit(userId);
                }

                return success;
            })
            .catch(this.handleError);
    }

    getRelatedUsers(userId: string): Promise<User[]> {
        const url = this.baseApiUrl + 'v1/user/' + userId + '/relationships';
        return this.http
            .get(url, { headers: this.adalService.createServiceHeader() })
            .toPromise()
            .then(resp => {
                return resp.json().body as User[];
            })
            .catch(this.handleError);
    }

    setCurrentUser(userEmail: string) {
        this.getUsersByEmail(userEmail)
            .then(response => {
                this.currentUser = response ? response.find(x => x.email.toUpperCase() === userEmail.toUpperCase()) : null;
                // if the currentUser comes back null it means the user isn't in loops
                // but should still have basic access to this system to create a dummy
                // user
                if (!this.currentUser) {
                    var newUser: User = new User();
                    newUser.name = this.adalService.userInfo.profile.name;
                    newUser.email = userEmail;

                    this.currentUser = newUser;
                }
                this.currentUserAuthenticated.emit(this.currentUser);
            })
            .catch(this.handleError);
    }

    getAssignedRegions(userId: string): Promise<Region[]> {
        var url = this.baseApiUrl + 'v1/userRegionAssignment/' + userId;
        return this.http
            .get(url, { headers: this.adalService.createServiceHeader() })
            .toPromise()
            .then(resp => {
                var regions = resp.json().body as Region[];
                var parsedRegions = JSON.parse(JSON.stringify(regions));
                return parsedRegions;
            })
            .catch(this.handleError);
    }

    saveAssignedRegions(userId: string, originalUserRegions: Region[], userRegions: Region[] ): void {
        var toRemove = originalUserRegions.filter((element) => {
            var include = true;
            userRegions.forEach(function (value) {
                if (value.name == element.name) {
                    include = false;
                }
            });

            return include;
        });

        var toAdd = userRegions.filter((element) => {
            var include = true;
            originalUserRegions.forEach(function (value) {
                if (value.name == element.name) {
                    include = false;
                }
            });

            return include;
        });

        var self = this;
        toRemove.forEach(function (value) {
           self.removeUserRegion(userId, value.id);
        });
        toAdd.forEach(function (value) {
            self.addUserRegion(userId, value.id);
        });

    }

    addUserRegion(userId: string, regionId: string): Promise<Region[]> {
        var url = this.baseApiUrl + 'v1/addUserRegion/' + userId + "/" + regionId;
        return this.http
            .get(url, { headers: this.adalService.createServiceHeader() })
            .toPromise()
            .then(resp => {
                var regions = resp.json().body as Region[];
                var parsedRegions = JSON.parse(JSON.stringify(regions));
                return parsedRegions;
            })
            .catch(this.handleError);
    }

    removeUserRegion(userId: string, regionId: string): Promise<Region[]> {
        var url = this.baseApiUrl + 'v1/removeUserRegion/' + userId + "/" + regionId;
        return this.http
            .get(url, { headers: this.adalService.createServiceHeader() })
            .toPromise()
            .then(resp => {
                var regions = resp.json().body as Region[];
                var parsedRegions = JSON.parse(JSON.stringify(regions));
                return parsedRegions;
            })
            .catch(this.handleError);
    }

    private handleError(errors: string[]): Promise<any> {
        console.error('An error occurred', errors);
        return Promise.reject(errors);
    }
}



// WEBPACK FOOTER //
// D:/a/1/s/Uhm.Loops.Web/src/app/users/user.service.ts